import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { useAuth } from "@/contexts/auth-context";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FormField } from "@/components/ui/form-field";
import { PasswordInput } from "@/components/ui/password-input";
import {
  forcedPasswordChangeSchema,
  type ForcedPasswordChangeValues,
} from "@/routes/forced-password-change/forced-password-change-schema";
import { toForcedChangeError } from "@/routes/forced-password-change/password-error";
import type { AuthBanner } from "@/routes/sign-in/auth-error";

// Story 10.9 — Screen 14 "Change password", on the Story 2.7 endpoint (schema +
// error mapper imported from the untouched forced-change screen). THREE fields, not
// the mockup's two: the endpoint requires current_password (sanctioned W3 deviation
// — a change without it would be a session-hijack primitive).

interface ChangePasswordResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  email: string;
  role: string;
  org_id: string | null;
}

export function ChangePasswordSection() {
  const { setSession } = useAuth();
  const [banner, setBanner] = React.useState<AuthBanner | null>(null);
  const [succeeded, setSucceeded] = React.useState(false);

  const form = useForm<ForcedPasswordChangeValues>({
    resolver: zodResolver(forcedPasswordChangeSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });

  const changeMutation = useMutation({
    mutationFn: (values: ForcedPasswordChangeValues) =>
      apiFetch<ChangePasswordResponse>("/auth/change-password", {
        method: "POST",
        body: {
          current_password: values.currentPassword,
          new_password: values.newPassword,
        },
        // skipAuthRefresh: a wrong-current-password 401 (AUTH-001) must surface
        // inline, not ride the silent-refresh → logout path.
        skipAuthRefresh: true,
      }),
    onSuccess: async (data) => {
      setBanner(null);
      // Adopt the fresh token FIRST — family revocation has already burned the
      // old refresh cookie server-side.
      await setSession(data.access_token);
      form.reset();
      setSucceeded(true);
    },
    onError: (error) => {
      setSucceeded(false);
      const mapped = toForcedChangeError(error);
      if (mapped.fieldName && mapped.fieldMessage) {
        form.setError(mapped.fieldName, { message: mapped.fieldMessage });
      }
      setBanner(mapped.banner);
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (changeMutation.isPending) return;
    setBanner(null);
    setSucceeded(false);
    changeMutation.mutate(values);
  });

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
          Change password
        </h2>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Update the password you use to sign in to CITS AMPS.
        </p>
      </div>

      <Card>
        {banner ? (
          <Banner variant={banner.variant} className="mb-4">
            {banner.message}
          </Banner>
        ) : null}
        {succeeded ? (
          <Banner variant="success" className="mb-4">
            Your password has been updated. Other signed-in sessions have been
            signed out.
          </Banner>
        ) : null}

        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <FormField
            label="Current password"
            error={form.formState.errors.currentPassword?.message}
            required
          >
            <PasswordInput
              autoComplete="current-password"
              {...form.register("currentPassword")}
            />
          </FormField>

          <FormField
            label="New password"
            error={form.formState.errors.newPassword?.message}
            helper="Minimum 8 characters."
            required
          >
            <PasswordInput
              autoComplete="new-password"
              {...form.register("newPassword")}
            />
          </FormField>

          <FormField
            label="Confirm new password"
            error={form.formState.errors.confirmPassword?.message}
            required
          >
            <PasswordInput
              autoComplete="new-password"
              {...form.register("confirmPassword")}
            />
          </FormField>

          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={changeMutation.isPending}
            >
              {changeMutation.isPending ? "Updating password…" : "Update password"}
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}
